// Lightweight auth context — tracks the current user across the app.
// On mount, tries /api/auth/me with the stored token. Login/register helpers
// set the token and refresh the user.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError } from "./api";
import { clearToken, getToken, setToken, type CurrentUser } from "./auth";

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_at: string;
}

interface AuthState {
  user: CurrentUser | null;
  ready: boolean;                // false until the initial /me probe settles
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signInWithGoogle: (credential: string) => Promise<void>;
  signOut: () => void;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setReady(true);
      return;
    }
    try {
      const me = await api.get<CurrentUser>("/api/auth/me");
      setUser(me);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearToken();
      }
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    const tok = await api.post<TokenResponse>(
      "/api/auth/login",
      { email, password },
      { auth: false },
    );
    setToken(tok.access_token);
    await refresh();
  }, [refresh]);

  const signUp = useCallback(async (email: string, password: string, name?: string) => {
    const tok = await api.post<TokenResponse>(
      "/api/auth/register",
      { email, password, name },
      { auth: false },
    );
    setToken(tok.access_token);
    await refresh();
  }, [refresh]);

  const signInWithGoogle = useCallback(async (credential: string) => {
    const tok = await api.post<TokenResponse>(
      "/api/auth/google",
      { credential },
      { auth: false },
    );
    setToken(tok.access_token);
    await refresh();
  }, [refresh]);

  const signOut = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, ready, signIn, signUp, signInWithGoogle, signOut }),
    [user, ready, signIn, signUp, signInWithGoogle, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
